import { JudgmentResult, JudgmentResultType, JudgmentResultEnum } from "../../types.client";
import { RsrpSatelliteMeasure } from "../../../models/schemas/measures/radioWave.schema";
import { RsrpBaseController } from "./RsrpBaseController";
import { RSRP_SATELLITE_THRESHOLDS } from "../../constants";

/**
 * 15. RSRP 위성방송 컨트롤러
 *
 * 판정 기준:
 * - DVB-S: SNR > 10dB
 * - DVB-S2: SNR 8~12dB
 * - MER ≥ 15dB
 * - RSSI: -65dBm ~ -25dBm 범위
 *
 * Note: DVB-S/DVB-S2 구분은 config.extra.standard로 설정
 */
export class RsrpSatelliteController extends RsrpBaseController<RsrpSatelliteMeasure> {
  protected getDefaultData(): RsrpSatelliteMeasure {
    return {
      snr: "",
      mer: "",
      ber: "",
      rssi: "",
      result: null,
      discussion: "",
    };
  }

  protected judge(): JudgmentResult {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    // 측정값이 하나도 없으면 NA
    if (snr === null && mer === null && rssi === null) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    let result: JudgmentResultType = JudgmentResultEnum.GOOD;

    // SNR 판정 (DVB-S: >10dB, DVB-S2: 8~12dB)
    if (snr !== null) {
      const isDvbS2 = this.config.extra?.standard === "DVB-S2";
      if (isDvbS2) {
        // DVB-S2: 8~12dB 범위
        if (
          snr < RSRP_SATELLITE_THRESHOLDS.snr.dvbS2.min ||
          snr > RSRP_SATELLITE_THRESHOLDS.snr.dvbS2.max
        ) {
          result = JudgmentResultEnum.BAD;
        }
      } else {
        // DVB-S: >10dB
        if (snr <= RSRP_SATELLITE_THRESHOLDS.snr.dvbS) {
          result = JudgmentResultEnum.BAD;
        }
      }
    }

    // MER 판정 (≥15dB)
    if (mer !== null && mer < RSRP_SATELLITE_THRESHOLDS.mer.min) {
      result = JudgmentResultEnum.BAD;
    }

    // RSSI 판정 (-65dBm ~ -25dBm)
    if (rssi !== null) {
      if (rssi < RSRP_SATELLITE_THRESHOLDS.rssi.min || rssi > RSRP_SATELLITE_THRESHOLDS.rssi.max) {
        result = JudgmentResultEnum.BAD;
      }
    }

    return {
      result,
      discussion: this.generateDiscussion(result),
    };
  }

  protected generateDiscussion(result: JudgmentResultType): string {
    const snr = this.parseSnr();
    const mer = this.parseMer();
    const rssi = this.parseRssi();

    if (result === JudgmentResultEnum.NA) {
      return "위성방송 RSRP 측정값이 입력되지 않았습니다.";
    }

    const isDvbS2 = this.config.extra?.standard === "DVB-S2";
    const standard = isDvbS2 ? "DVB-S2" : "DVB-S";

    const parts: string[] = [];
    if (snr !== null) {
      let snrStatus: string;
      if (isDvbS2) {
        snrStatus =
          snr >= RSRP_SATELLITE_THRESHOLDS.snr.dvbS2.min &&
          snr <= RSRP_SATELLITE_THRESHOLDS.snr.dvbS2.max
            ? "양호"
            : "불량";
        parts.push(
          `SNR ${snr}dB (${standard} 기준 ${RSRP_SATELLITE_THRESHOLDS.snr.dvbS2.min}~${RSRP_SATELLITE_THRESHOLDS.snr.dvbS2.max}dB, ${snrStatus})`
        );
      } else {
        snrStatus = snr > RSRP_SATELLITE_THRESHOLDS.snr.dvbS ? "양호" : "불량";
        parts.push(
          `SNR ${snr}dB (${standard} 기준 >${RSRP_SATELLITE_THRESHOLDS.snr.dvbS}dB, ${snrStatus})`
        );
      }
    }
    if (mer !== null) {
      const merStatus = mer >= RSRP_SATELLITE_THRESHOLDS.mer.min ? "양호" : "불량";
      parts.push(`MER ${mer}dB (기준 ≥${RSRP_SATELLITE_THRESHOLDS.mer.min}dB, ${merStatus})`);
    }
    if (rssi !== null) {
      const rssiStatus =
        rssi >= RSRP_SATELLITE_THRESHOLDS.rssi.min && rssi <= RSRP_SATELLITE_THRESHOLDS.rssi.max
          ? "양호"
          : "불량";
      parts.push(
        `RSSI ${rssi}dBm (기준 ${RSRP_SATELLITE_THRESHOLDS.rssi.min}~${RSRP_SATELLITE_THRESHOLDS.rssi.max}dBm, ${rssiStatus})`
      );
    }

    return parts.join(", ");
  }
}
